import { parse, startOfDay, subMonths, subYears } from 'date-fns';
import { ParsedRecord } from 'src/components/import/ImportParser';

import { parseStringToNumberOrUndefined } from '../../parseNumber';
import { locationEdcAccountNumber } from '../validators';
import { PjmRegistrationDto } from '../dto/PjmRegistrationDto';

export const registrationMeterDataUploadCsvConfig = [
    {
        column: 'Registration',
        required: true,
        description: 'Id of the Registration',
        width: 200,
    },
    {
        column: 'Account',
        required: true,
        description: 'Only letters and numbers',
        width: 200,
    },
    {
        column: 'Date',
        required: true,
        description: `Date in format MM/DD/YYYY for example: 01/31/2025`,
        width: 150,
    },
    {
        column: 'Type',
        required: true,
        description: `Allowed only: Hourly_Load`,
        width: 150,
    },
    {
        column: 'UOM',
        required: true,
        description: `Allowed only: KW`,
        width: 100,
    },
    {
        column: 'HE1',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE2',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE3',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE4',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE5',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE6',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE7',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE8',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE9',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE10',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE11',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE12',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE13',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE14',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE15',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE16',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE17',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE18',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE19',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE20',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE21',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE22',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE23',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE24',
        required: true,
        description: `Only positive numbers`,
        width: 100,
    },
    {
        column: 'HE25',
        required: false,
        description: ``,
        width: 100,
    },
] as const;

export type PjmRegistrationMeterDataUploadCsvColumns = (typeof registrationMeterDataUploadCsvConfig)[number]['column'];

export const pjmRegistrationMeterDataUploadParser = (registrations: PjmRegistrationDto[]) => {
    return function (row: ParsedRecord<PjmRegistrationMeterDataUploadCsvColumns>): ParsedRecord<PjmRegistrationMeterDataUploadCsvColumns> {
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'Registration');

        const registrationId: string = row['Registration'].value;

        const registration = registrations.find(reg => reg.id === Number(registrationId));

        if (!registration?.id) row['Registration'].error = `Registration with id: ${registrationId} not found.`;

        const account: string = row['Account'].value;

        row = isNotEmptyStringHandler<PjmRegistrationMeterDataUploadCsvColumns>(row, 'Account', [edcAccountNumberHasCorrectFormat(account)]);

        if (registration?.location?.summaryInfo?.EDC?.accountNo !== account) row['Account'].error = `Account does not match an EDC account number.`;

        const startRange = subYears(subMonths(new Date(), 6), 2); // allowed up to 2 years and 6 months before the current date
        const endRange = startOfDay(new Date()); // allowed up to the current date
        row = isDateHandler<PjmRegistrationMeterDataUploadCsvColumns>(row, 'Date', [dateInRange(startRange, endRange)]);

        row = isTheSame<PjmRegistrationMeterDataUploadCsvColumns>(row, 'Type', 'Hourly_Load');
        row = isTheSame<PjmRegistrationMeterDataUploadCsvColumns>(row, 'UOM', 'KW');

        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE1', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE2', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE3', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE4', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE5', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE6', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE7', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE8', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE9', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE10', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE11', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE12', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE13', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE14', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE15', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE16', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE17', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE18', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE19', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE20', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE21', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE22', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE23', numberIsBigger(0));
        row = isNumber<PjmRegistrationMeterDataUploadCsvColumns>(row, 'HE24', numberIsBigger(0));

        return row;
    };
};

function isNotEmptyStringHandler<T extends string>(row: ParsedRecord<T>, key: T, extraValidations?: Array<(value: T) => Error | null>) {
    if (row[key].value === '') {
        row[key].error = `"${key}" is required`;
    } else {
        row[key].parsedValue = row[key].value;
        if (extraValidations?.length) {
            extraValidations.forEach(fn => {
                const error = fn(row[key].value);
                if (error) {
                    row[key].error = `Field "${key}": ${error.message}`;
                }
            });
        }
    }

    return row;
}

function isNumber<T extends string>(row: ParsedRecord<T>, key: T, extraValidationFn?: (value: number) => Error | null) {
    const num = parseStringToNumberOrUndefined(row[key].value);
    if (num === undefined) {
        row[key].error = `"${key}" is not a number`;
    } else {
        row[key].parsedValue = num;
        if (extraValidationFn) {
            const error = extraValidationFn(num);
            if (error) {
                row[key].error = `Field "${key}": ${error.message}`;
            }
        }
    }

    return row;
}

function isDateHandler<T extends string>(row: ParsedRecord<T>, key: T, extraValidations?: Array<(value: Date) => Error | null>) {
    if (row[key].value === '') {
        row[key].error = `"${key}" is required`;
        return row;
    }

    const date = parse(row[key].value, 'MM/dd/yyyy', new Date());
    if (!date) {
        row[key].error = `"${key}" is invalid date, use format MM/DD/YYYY`;
        return row;
    }

    if (extraValidations?.length) {
        extraValidations.forEach(fn => {
            const error = fn(date);
            if (error) {
                row[key].error = `Field "${key}": ${error.message}`;
            }
        });
    }

    row[key].parsedValue = date.toISOString();

    return row;
}

function isTheSame<T extends string>(row: ParsedRecord<T>, key: T, allowedOption: Readonly<string> | string) {
    const val = String(row[key].value).toLowerCase();
    if (val !== allowedOption.toLowerCase()) {
        row[key].error = `"${key}" must be: ${JSON.stringify(allowedOption)}`;
    }

    row[key].parsedValue = allowedOption;

    return row;
}

function numberIsBigger(min: number) {
    return (val: number) => {
        if (val < min) {
            return new Error(`Number should be equal or bigger than ${min}`);
        }
        return null;
    };
}

function dateInRange(min: Date, max: Date) {
    return (val: Date) => {
        if (val.valueOf() < min.valueOf()) {
            return new Error(`Date is before the oldest allowable date`);
        }

        if (val.valueOf() >= max.valueOf()) {
            return new Error(`Date is on or after the current date`);
        }
        return null;
    };
}

function edcAccountNumberHasCorrectFormat(edcName: string) {
    return (val: string) => {
        const result = locationEdcAccountNumber.isValid(val, edcName);
        if (result.valid) return null;

        return result.error;
    };
}
